import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/useAuthStore';
import { usePieceStore } from '../stores/usePieceStore';
import { supabase } from '../supabase';
import { LogOut, Save, Plus, Trash2, Tag, Lock } from 'lucide-react';
import { clsx } from 'clsx';
import { DS } from '../theme/DesignSystem';
import { BottomDock } from '../components/Layout/BottomDock';

export const Settings: React.FC = () => {
    const navigate = useNavigate();
    const { signOut, userProfile } = useAuthStore();
    const { defectsConfig, fetchSystemConfig, updateSystemConfig, fetchTailors, tailors } = usePieceStore();

    const [activeTab, setActiveTab] = useState<'defects' | 'tailors'>('defects');
    const [localDefects, setLocalDefects] = useState<Record<string, string[]>>({});
    const [localTailors, setLocalTailors] = useState<string[]>([]);
    const [newTailorName, setNewTailorName] = useState('');
    const [hasChanges, setHasChanges] = useState(false);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [newItemName, setNewItemName] = useState('');
    const [addingItemTo, setAddingItemTo] = useState<string | null>(null);

    const isAdmin = userProfile?.role === 'admin';

    useEffect(() => { fetchSystemConfig(); fetchTailors(); }, []);

    useEffect(() => {
        if (defectsConfig?.items) {
            setLocalDefects(JSON.parse(JSON.stringify(defectsConfig.items)));
        } else {
            setLocalDefects({
                "Fabric Defects": ["Hole", "Tear", "Stain", "Shade Variation"],
                "Stitching Defects": ["Open Seam", "Broken Stitch", "Skip Stitch"]
            });
        }
    }, [defectsConfig]);

    useEffect(() => { setLocalTailors(tailors.map(t => t.full_name)); }, [tailors]);

    const handleSave = async () => {
        if (!navigator.onLine) { alert("No internet connection."); return; }
        if (activeTab === 'defects') {
            await updateSystemConfig(localDefects);
        } else {
            const { error } = await supabase.from('system_config').upsert({
                factory_id: userProfile?.factory_id, category: 'tailors', items: localTailors
            }, { onConflict: 'factory_id,category' });
            if (!error) await fetchTailors();
        }
        setHasChanges(false);
    };

    const deleteCategory = (cat: string) => { const next = { ...localDefects }; delete next[cat]; setLocalDefects(next); setHasChanges(true); };
    const addItem = (cat: string) => {
        if (!newItemName.trim()) return;
        const next = { ...localDefects };
        if (!next[cat].includes(newItemName)) { next[cat] = [...next[cat], newItemName]; setLocalDefects(next); setHasChanges(true); }
        setNewItemName(''); setAddingItemTo(null);
    };
    const deleteItem = (cat: string, item: string) => { const next = { ...localDefects }; next[cat] = next[cat].filter(i => i !== item); setLocalDefects(next); setHasChanges(true); };
    const addNewCategory = () => {
        if (!newCategoryName.trim()) return;
        const next = { ...localDefects };
        if (!next[newCategoryName]) { next[newCategoryName] = []; setLocalDefects(next); setHasChanges(true); }
        setNewCategoryName('');
    };
    const addTailor = () => {
        if (!isAdmin || !newTailorName.trim()) return;
        if (!localTailors.includes(newTailorName)) { setLocalTailors([...localTailors, newTailorName]); setHasChanges(true); }
        setNewTailorName('');
    };
    const removeTailor = (name: string) => { if (!isAdmin) return; setLocalTailors(localTailors.filter(t => t !== name)); setHasChanges(true); };

    return (
        <div className={clsx("min-h-screen flex flex-col", DS.layout.bg)}>
            {/* Header */}
            <div className="bg-white/80 backdrop-blur-2xl pt-safe px-5 pb-3 border-b border-slate-100/80 sticky top-0 z-40">
                <div className="flex items-center justify-between py-3">
                    <h1 className="text-xl font-bold text-slate-800">Settings</h1>
                    {hasChanges && (
                        <button
                            onClick={handleSave}
                            className="bg-blue-600 text-white px-4 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-sm shadow-blue-600/20 active:scale-95 transition-all"
                        >
                            <Save size={12} /> Save
                        </button>
                    )}
                </div>
            </div>

            <div className="flex-1 overflow-y-auto px-5 pb-24 animate-page">
                {/* App Identity */}
                <div className="py-6 flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-600 rounded-xl shadow-md shadow-blue-600/20 flex items-center justify-center">
                        <span className="text-lg font-bold text-white">S</span>
                    </div>
                    <div>
                        <h2 className="text-slate-800 font-bold text-sm">SampleIntel</h2>
                        <p className="text-slate-400 text-[10px] font-medium">Factory OS v2.0</p>
                    </div>
                </div>

                {/* Tab Switcher */}
                <div className="mb-5">
                    <div className="bg-slate-100/80 p-1 rounded-xl flex">
                        {['defects', 'tailors'].map(tab => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab as any)}
                                className={clsx(
                                    "flex-1 py-2.5 rounded-lg text-xs font-bold transition-all duration-200 capitalize",
                                    activeTab === tab
                                        ? "bg-white text-slate-800 shadow-sm"
                                        : "text-slate-400"
                                )}
                            >
                                {tab}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Content */}
                <div className="space-y-3">
                    {activeTab === 'defects' && (
                        <>
                            {/* Add Category */}
                            <div className="bg-blue-50/60 rounded-2xl p-4 border border-blue-100/80">
                                <p className="text-[10px] font-bold text-blue-600 uppercase tracking-wider mb-2">New Category</p>
                                <div className="flex gap-2">
                                    <input
                                        value={newCategoryName}
                                        onChange={(e) => setNewCategoryName(e.target.value)}
                                        placeholder="e.g. Trim Defects"
                                        className="flex-1 bg-white border border-blue-100 rounded-xl px-3.5 py-2.5 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 font-medium placeholder:text-slate-300"
                                        onKeyDown={(e) => e.key === 'Enter' && addNewCategory()}
                                    />
                                    <button onClick={addNewCategory} className="bg-white text-blue-600 p-2.5 rounded-xl border border-blue-100 active:bg-blue-50 transition-colors">
                                        <Plus size={16} />
                                    </button>
                                </div>
                            </div>

                            {/* Categories */}
                            {Object.entries(localDefects).map(([cat, items]) => (
                                <div key={cat} className="bg-white rounded-2xl p-4 border border-slate-100 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
                                    <div className="flex justify-between items-center mb-3">
                                        <div className="flex items-center gap-2">
                                            <Tag size={12} className="text-blue-500" />
                                            <span className="font-bold text-sm text-slate-700">{cat}</span>
                                        </div>
                                        <button onClick={() => deleteCategory(cat)} className="text-slate-300 hover:text-red-400 p-1 transition-colors">
                                            <Trash2 size={14} />
                                        </button>
                                    </div>

                                    <div className="flex flex-wrap gap-1.5 mb-3">
                                        {items.map(item => (
                                            <span key={item} className="inline-flex items-center gap-1 px-2.5 py-1 bg-slate-50 border border-slate-100 rounded-lg text-[10px] font-medium text-slate-600">
                                                {item}
                                                <button onClick={() => deleteItem(cat, item)} className="ml-0.5 text-slate-300 hover:text-red-400 text-sm leading-none">×</button>
                                            </span>
                                        ))}
                                    </div>

                                    {addingItemTo === cat ? (
                                        <div className="flex gap-2 items-center">
                                            <input
                                                autoFocus value={newItemName}
                                                onChange={(e) => setNewItemName(e.target.value)}
                                                className="flex-1 bg-slate-50 rounded-lg px-3 py-2 text-xs text-slate-800 focus:outline-none border border-blue-300 font-medium"
                                                placeholder="Item name..."
                                                onKeyDown={(e) => e.key === 'Enter' && addItem(cat)}
                                            />
                                            <button onClick={() => addItem(cat)} className="text-blue-600 text-xs font-bold">Add</button>
                                            <button onClick={() => setAddingItemTo(null)} className="text-slate-400 text-xs">Cancel</button>
                                        </div>
                                    ) : (
                                        <button onClick={() => setAddingItemTo(cat)} className="flex items-center gap-1 text-[10px] font-bold text-blue-600 px-1 py-1 transition-colors">
                                            <Plus size={10} /> Add Item
                                        </button>
                                    )}
                                </div>
                            ))}
                        </>
                    )}

                    {activeTab === 'tailors' && (
                        <>
                            <div className="p-3.5 rounded-xl bg-blue-50/60 border border-blue-100/80 text-blue-700 text-xs font-medium">
                                {isAdmin ? "Manage tailor assignments." : "View-only. Contact admin for changes."}
                            </div>

                            {isAdmin && (
                                <div className="bg-blue-50/60 rounded-2xl p-4 border border-blue-100/80">
                                    <p className="text-[10px] font-bold text-blue-600 uppercase tracking-wider mb-2">Add Tailor</p>
                                    <div className="flex gap-2">
                                        <input
                                            value={newTailorName}
                                            onChange={(e) => setNewTailorName(e.target.value)}
                                            placeholder="Full Name..."
                                            className="flex-1 bg-white border border-blue-100 rounded-xl px-3.5 py-2.5 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 font-medium placeholder:text-slate-300"
                                            onKeyDown={(e) => e.key === 'Enter' && addTailor()}
                                        />
                                        <button onClick={addTailor} className="bg-white text-blue-600 p-2.5 rounded-xl border border-blue-100 active:bg-blue-50 transition-colors">
                                            <Plus size={16} />
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div className="space-y-1.5">
                                {localTailors.map((name, i) => (
                                    <div
                                        key={name}
                                        className="bg-white p-3.5 rounded-xl border border-slate-100 shadow-[0_1px_3px_rgba(0,0,0,0.03)] flex items-center justify-between animate-card"
                                        style={{ animationDelay: `${i * 30}ms` }}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600 text-xs font-bold">
                                                {name.charAt(0)}
                                            </div>
                                            <span className="font-semibold text-slate-700 text-sm">{name}</span>
                                        </div>
                                        {isAdmin ? (
                                            <button onClick={() => removeTailor(name)} className="text-slate-300 hover:text-red-400 p-1.5 transition-colors">
                                                <Trash2 size={14} />
                                            </button>
                                        ) : (
                                            <Lock size={12} className="text-slate-300" />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </>
                    )}

                    {/* Sign Out */}
                    <button
                        onClick={() => signOut().then(() => navigate('/login'))}
                        className="w-full py-3.5 mt-6 bg-red-50 border border-red-100 text-red-500 font-bold text-sm rounded-2xl flex items-center justify-center gap-2 active:bg-red-100 transition-colors"
                    >
                        <LogOut size={16} /> Sign Out
                    </button>
                </div>
            </div>

            <BottomDock />
        </div>
    );
};